import { Head, router } from '@inertiajs/react'
import { useTranslation } from 'react-i18next'
import { AdminLayout } from '@/layouts/admin-layout'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Switch } from '@/components/ui/switch'
import { Badge } from '@/components/ui/badge'

interface GlobalFeature {
  name: string
  enabled: boolean
}

interface OrgFeature {
  name: string
  enabled: boolean
}

interface OrganizationWithFeatures {
  id: number
  name: string
  current_plan: string
  features: OrgFeature[]
}

interface Props {
  global_features: GlobalFeature[]
  organization_features: OrganizationWithFeatures[]
  available_org_features: string[]
}

export default function FeaturesIndex({ global_features, organization_features, available_org_features }: Props) {
  const { t } = useTranslation('admin')

  const handleToggleGlobal = (featureName: string) => {
    router.post('/admin/features/toggle_global', { feature_name: featureName })
  }

  const handleToggleInstance = (organizationId: number, featureName: string) => {
    router.post('/admin/features/toggle_instance', {
      organization_id: organizationId,
      feature_name: featureName,
    })
  }

  const isGloballyEnabled = (featureName: string) => {
    return global_features.find(f => f.name === featureName)?.enabled ?? false
  }

  return (
    <>
      <Head title={t('featureFlags.title')} />
      <div className="flex flex-1 flex-col">
        <div className="flex flex-1 flex-col gap-4 py-4 md:gap-6 md:py-6">
          <div className="px-4 lg:px-6">
            <h1 className="text-2xl font-bold mb-6">{t('featureFlags.title')}</h1>

            <Card className="mb-8">
              <CardHeader>
                <CardTitle className="text-lg">{t('featureFlags.globalFeatures')}</CardTitle>
                <p className="text-sm text-muted-foreground">
                  {t('featureFlags.globalDescription')}
                </p>
              </CardHeader>
              <CardContent>
                <div className="divide-y">
                  {global_features.map(feature => (
                    <div key={feature.name} className="flex items-center justify-between py-3">
                      <div>
                        <span className="font-medium font-mono text-sm">{feature.name}</span>
                        {feature.enabled && (
                          <Badge variant="secondary" className="ml-2">{t('featureFlags.globalOn')}</Badge>
                        )}
                      </div>
                      <Switch
                        checked={feature.enabled}
                        onCheckedChange={() => handleToggleGlobal(feature.name)}
                      />
                    </div>
                  ))}
                  {global_features.length === 0 && (
                    <p className="text-sm text-muted-foreground py-3">{t('featureFlags.noGlobalFeatures')}</p>
                  )}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-lg">{t('featureFlags.orgFeatures')}</CardTitle>
                <p className="text-sm text-muted-foreground">
                  {t('featureFlags.orgDescription')}
                </p>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left py-2 pr-2 font-medium w-10">{t('featureFlags.id')}</th>
                        <th className="text-left py-2 pr-4 font-medium">{t('featureFlags.organization')}</th>
                        <th className="text-left py-2 pr-4 font-medium">{t('featureFlags.plan')}</th>
                        {available_org_features.map(name => (
                          <th key={name} className="text-center py-2 px-2 font-medium font-mono">{name}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {organization_features.map(org => (
                        <tr key={org.id}>
                          <td className="py-2 pr-2 text-muted-foreground text-xs w-10">{org.id}</td>
                          <td className="py-2 pr-4">{org.name}</td>
                          <td className="py-2 pr-4">
                            <Badge variant="outline">{org.current_plan}</Badge>
                          </td>
                          {org.features.map(feature => {
                            const globalOn = isGloballyEnabled(feature.name)
                            return (
                              <td key={feature.name} className="text-center py-2 px-2">
                                <Switch
                                  checked={feature.enabled || globalOn}
                                  disabled={globalOn}
                                  onCheckedChange={() => handleToggleInstance(org.id, feature.name)}
                                  className={globalOn ? 'opacity-50' : ''}
                                />
                              </td>
                            )
                          })}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </>
  )
}

FeaturesIndex.layout = (page: React.ReactNode) => <AdminLayout>{page}</AdminLayout>
